import { z } from 'zod'

import { getExchangeToday } from '@/lib/exchange-time'

// ─── Operators ───

export const Operator = z.enum([
  'gt',
  'gte',
  'lt',
  'lte',
  'eq',
  'neq',
  'between',
  'contains',
  'not_contains',
  'in',
  'not_in',
  'is_true',
  'is_false',
])
export type Operator = z.infer<typeof Operator>

export const OPERATOR_LABELS: Record<Operator, string> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  eq: '=',
  neq: '!=',
  between: 'BTW',
  contains: 'Contains',
  not_contains: 'Not Contains',
  in: 'In',
  not_in: 'Not In',
  is_true: 'Is True',
  is_false: 'Is False',
}

// ─── Data Types ───

export const DataType = z.enum([
  'number',
  'percent',
  'currency',
  'volume',
  'string',
  'boolean',
  'enum',
  'date',
])
export type DataType = z.infer<typeof DataType>

export const OPERATORS_BY_DATA_TYPE: Record<DataType, Operator[]> = {
  number: ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between'],
  percent: ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between'],
  currency: ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between'],
  volume: ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between'],
  string: ['eq', 'neq', 'contains', 'not_contains', 'in', 'not_in'],
  boolean: ['is_true', 'is_false'],
  enum: ['in', 'not_in'],
  date: ['gte', 'lte', 'eq', 'between'],
}

/**
 * Time-valued columns (day0_high_time, day0_low_time, …) carry
 * `dataType: 'string'` so they pair with each other and not with `ticker`, but
 * string operators are the wrong vocabulary for a clock time: nobody filters a
 * high time with `contains`, and the useful question ("before 10:00") was not
 * expressible at all. The live history routes compile these against MotherDuck
 * `TIME` columns and the backend's `ComparisonOperator` is column-agnostic, so
 * the comparisons compile; the value must carry seconds (`HH:mm:ss`), which
 * `toHhMmSs` adds at query time (MotherDuck cannot decode `15:00` into TIME).
 */
export const TIME_COLUMN_OPERATORS: Operator[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between']

/**
 * The operators a column may use. Prefer this over indexing
 * `OPERATORS_BY_DATA_TYPE` directly: the picker, the reducer's
 * reset-on-column-change, and the validator must all agree, or the form offers
 * an operator its own validator then rejects.
 *
 * Structurally typed rather than taking a `ColumnCatalogDef` so this module
 * stays free of an import cycle through the catalog types. Generic rather than
 * a plain structural parameter so a caller may pass a column literal without
 * tripping excess-property checking on the fields this does not read.
 */
export function operatorsForColumn<T extends { dataType: DataType; columnType?: string }>(
  col: T | undefined,
): Operator[] {
  if (!col) return []
  if (col.columnType === 'string_time') return TIME_COLUMN_OPERATORS
  return OPERATORS_BY_DATA_TYPE[col.dataType]
}

// ─── Type classes (coarse tier, for column-to-column comparison) ───

export const TYPE_CLASS = ['numeric', 'temporal', 'categorical', 'boolean'] as const
export type TypeClass = (typeof TYPE_CLASS)[number]

export const DATA_TYPE_TO_CLASS: Record<DataType, TypeClass> = {
  number: 'numeric',
  percent: 'numeric',
  currency: 'numeric',
  volume: 'numeric',
  string: 'categorical',
  enum: 'categorical',
  boolean: 'boolean',
  date: 'temporal',
}

export const typeClassOf = (dt: DataType): TypeClass => DATA_TYPE_TO_CLASS[dt]

/**
 * Compatibility strictness.
 *
 * - `'strict'` (default) — exact `DataType` match. Used for column-to-column comparison:
 *   currency ↔ currency only, percent ↔ percent only. Prevents semantically weird pairings
 *   like price > rvol.
 * - `'class'` — same `TypeClass`. Used when one side is an expression whose output type is
 *   declared at a coarser granularity (any numeric formula vs any numeric column).
 */
export type CompatMode = 'strict' | 'class'

export const areDataTypesCompatible = (
  a: DataType,
  b: DataType,
  mode: CompatMode = 'strict',
): boolean => {
  if (mode === 'strict') return a === b
  return typeClassOf(a) === typeClassOf(b)
}

// ─── Right Value (polymorphic) ───

export const RangeValue = z.object({
  low: z.union([z.number(), z.string()]),
  high: z.union([z.number(), z.string()]),
})
export type RangeValue = z.infer<typeof RangeValue>

// ─── Time Slices ───

export const TimeSlice = z.object({
  start: z.string(),
  end: z.string(),
  value: z.number().nullable(),
})
export type TimeSlice = z.infer<typeof TimeSlice>

export const TimeSliceValue = z.array(TimeSlice)
export type TimeSliceValue = z.infer<typeof TimeSliceValue>

export const RightValue = z.union([
  z.number(),
  z.string(),
  z.boolean(),
  z.array(z.string()),
  RangeValue,
  TimeSliceValue,
  z.null(),
])
export type RightValue = z.infer<typeof RightValue>

export const RightType = z.enum(['value', 'column', 'time_slices'])
export type RightType = z.infer<typeof RightType>

// ─── Column Reference ───

export const ColumnRef = z.object({
  column: z.string().min(1, 'Column is required'),
  timeframe: z.string().optional(),
  aggregate: z.string().optional(),
})
export type ColumnRef = z.infer<typeof ColumnRef>

/**
 * Whether a row still lacks the right-hand side its operator needs: a value, the column to compare
 * with, or a value in every time window. The schema refuses such a row, which is what keeps Apply off, and the row marks the gap
 * (Chris, 2026-10-10). Boolean operators need none: their right is synthesized when serialized.
 */
export function isRightMissing(row: {
  operator: string
  rightType: RightType
  right: unknown
}): boolean {
  if (row.operator === 'is_true' || row.operator === 'is_false') return false
  if (row.rightType === 'column') return typeof row.right !== 'string' || row.right.length === 0
  if (row.rightType === 'time_slices') {
    return !isTimeSliceValue(row.right) || row.right.some((s) => s.value === null)
  }
  return row.rightType === 'value' && row.right === null
}

// ─── Filter Entity Fields (validated by RHF + Zod) ───

export const FilterEntityFieldsSchema = z
  .object({
    left: ColumnRef,
    operator: Operator,
    right: RightValue,
    rightType: RightType,
    enabled: z.boolean(),
    /**
     * When true the backend ORs `IS NULL` onto this leaf, so rows with no
     * value for the column pass instead of being excluded. Legacy presets
     * set it on cap/float ceilings so small caps with missing fundamentals
     * still scan; ettalchemy honors it per leaf. Absent means false.
     */
    allowNull: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Column right-type requires a non-empty string; value right-type a non-null right.
    if (isRightMissing(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: data.rightType === 'column' ? 'Right column is required' : 'Value is required',
        path: ['right'],
      })
    }
  })
export type FilterEntityFields = z.infer<typeof FilterEntityFieldsSchema>

export function isRangeValue(value: unknown): value is RangeValue {
  return typeof value === 'object' && value !== null && 'low' in value && 'high' in value
}

export function isTimeSliceValue(value: unknown): value is TimeSliceValue {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'start' in value[0] &&
    'end' in value[0]
  )
}

export function createDefaultTimeSlices(): TimeSliceValue {
  return [
    { start: '04:00', end: '09:30', value: null },
    { start: '09:30', end: '20:00', value: null },
  ]
}

export function createDefaultEntity(column = ''): FilterEntityFields {
  return {
    left: { column },
    operator: 'gt',
    right: null,
    rightType: 'value',
    enabled: true,
  }
}

// ─── Rolling Date Options ───

export interface RollingDateOption {
  key: string
  label: string
  value: number
  unit: 'days' | 'months' | 'years'
}

export const ROLLING_DATE_OPTIONS: RollingDateOption[] = [
  { key: 'rolling_7_days', label: '7 Days', value: 7, unit: 'days' },
  { key: 'rolling_30_days', label: '30 Days', value: 30, unit: 'days' },
  { key: 'rolling_60_days', label: '60 Days', value: 60, unit: 'days' },
  { key: 'rolling_90_days', label: '90 Days', value: 90, unit: 'days' },
  { key: 'rolling_3_months', label: '3 Months', value: 3, unit: 'months' },
  { key: 'rolling_6_months', label: '6 Months', value: 6, unit: 'months' },
  { key: 'rolling_1_years', label: '1 Year', value: 1, unit: 'years' },
  { key: 'rolling_2_years', label: '2 Years', value: 2, unit: 'years' },
  { key: 'rolling_3_years', label: '3 Years', value: 3, unit: 'years' },
  { key: 'rolling_5_years', label: '5 Years', value: 5, unit: 'years' },
  { key: 'rolling_14_years', label: '14 Years', value: 14, unit: 'years' },
]

export function isRollingDateValue(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('rolling_')
}

export function getRollingDateLabel(value: string): string | null {
  const opt = ROLLING_DATE_OPTIONS.find((o) => o.key === value)
  return opt?.label ?? null
}

export function createDefaultDateEntity(
  column: string,
  defaultLow: string = 'rolling_90_days',
): FilterEntityFields {
  const today = getExchangeToday()
  return {
    left: { column },
    operator: 'between',
    right: { low: defaultLow, high: today },
    rightType: 'value',
    enabled: true,
  }
}
